import React from "react";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Divider, Typography } from "@mui/material";
import Button from "@components/Button/Button";
import BaseTable from "@components/DataTable/BaseTable";
import { Pagination } from "@components/Pagination/Pagination";
import type { CargoOnboardParcelFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { operationTypes, REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Cargo, CustomerTerminal } from "@services/vessel-nomination/types";
import { useTranslation } from "react-i18next";
import AddParcelSideDrawer from "./ParcelSideDrawer";

const LANG_BASE = "vessel-nomination.add.form.parcels";
const LANG_BASE_HEADER = `${LANG_BASE}.headers`;
const DEFAULT_PAGE_SIZE = 10;

interface ParcelsProps {
  parcels: CargoOnboardParcelFormData[];
  parcelsError?: string;
  setParcels: (parcels: CargoOnboardParcelFormData[]) => void;
  clearParcelsError: () => void;
}

const Parcels: React.FC<ParcelsProps> = ({
  parcels,
  parcelsError,
  setParcels,
  clearParcelsError,
}) => {
  const { t } = useTranslation();
  const [sideDrawerOpen, setSideDrawerOpen] = React.useState(false);
  const [editingParcelIndex, setEditingParcelIndex] = React.useState<number | null>(null);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);

  const headers = [
    { field: "parcelSequence", columnName: t(`${LANG_BASE_HEADER}.parcel-sequence`) },
    { field: "terminalName", columnName: t(`${LANG_BASE_HEADER}.terminal-name`) },
    { field: "operationType", columnName: t(`${LANG_BASE_HEADER}.operation-type`) },
    { field: "cargoType", columnName: t(`${LANG_BASE_HEADER}.cargo-type`) },
    { field: "cargoQuantityCbm", columnName: t(`${LANG_BASE_HEADER}.cargo-quantity-cbm`) },
    { field: "cargoQuantityMt", columnName: t(`${LANG_BASE_HEADER}.cargo-quantity-mt`) },
    { field: "action", columnName: t(`${LANG_BASE_HEADER}.action`) },
  ];

  const { options: cargoTypes } = useFormattedReference<Cargo>(
    `${REFERENCE_DATA_TYPES.CARGO}?cargoTypeCodes=LB,FO,CO`,
    "bff-vessel",
  );

  const { options: terminalOptions } = useFormattedReference<CustomerTerminal>(
    REFERENCE_DATA_TYPES.CUSTOMER_TERMINAL,
    "bff-vessel",
  );

  const rows = parcels.map(
    (
      {
        terminal,
        operationType,
        cargoType,
        cargoQuantityCbm,
        cargoQuantityMt,
      }: CargoOnboardParcelFormData,
      index: number,
    ) => ({
      id: index + 1,
      parcelIndex: index,
      parcelSequence: index + 1,
      terminalName:
        terminalOptions.find((t) => t.companyCode === terminal)?.companyNameEnglish ?? terminal,
      operationType:
        operationTypes.find((operation) => operation.value === operationType)?.label ??
        operationType,
      cargoType:
        cargoTypes.find((type) => type.cargoCode === cargoType)?.cargoDescription ?? cargoType,
      cargoQuantityCbm: cargoQuantityCbm,
      cargoQuantityMt: cargoQuantityMt,
    }),
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  React.useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedRows = React.useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return rows.slice(startIndex, startIndex + pageSize);
  }, [page, pageSize, rows]);

  const handleDeleteParcel = (row: { parcelIndex: number }) => {
    const updatedParcels = parcels.filter((_: CargoOnboardParcelFormData, index: number) => {
      return index !== row.parcelIndex;
    });

    setParcels(updatedParcels);
    clearParcelsError();
  };

  const handleEditParcel = (row: { parcelIndex: number }) => {
    setEditingParcelIndex(row.parcelIndex);
    setSideDrawerOpen(true);
  };

  const handleAddParcel = () => {
    setEditingParcelIndex(null);
    setSideDrawerOpen(true);
  };

  const handleCloseSideDrawer = () => {
    setEditingParcelIndex(null);
    setSideDrawerOpen(false);
  };

  const handleSideDrawerSuccess = () => {
    setEditingParcelIndex(null);
    setSideDrawerOpen(false);
  };

  return (
    <div className="flex flex-col items-end gap-4" data-testid="">
      <div className="flex flex-col w-full gap-4">
        <BaseTable
          rows={paginatedRows}
          headers={headers}
          onRowEdit={handleEditParcel}
          onRowDelete={handleDeleteParcel}
        />
        {rows.length > 10 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={rows.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            showPageSizeOptions={false}
          />
        )}
      </div>
      <Divider className="w-full" />
      <div className="flex w-full justify-between items-center">
        <Typography color="error" variant="caption" sx={{ fontWeight: 500 }}>
          {parcelsError ?? ""}
        </Typography>
      </div>
      <Button
        type="button"
        variant="outlined"
        color="primary"
        testId="page-header-register-vessel-button"
        label={
          <span className="text-sm font-medium flex items-center gap-2">
            <AddCircleOutline fontSize="small" />
            {t(`${LANG_BASE}.side-drawer.add-parcel`)}
          </span>
        }
        onClick={handleAddParcel}
        sx={{ textTransform: "none", height: "40px", minHeight: "40px" }}
      />
      <AddParcelSideDrawer
        parcels={parcels}
        setParcels={setParcels}
        clearParcelsError={clearParcelsError}
        isOpen={sideDrawerOpen}
        onClose={handleCloseSideDrawer}
        onSuccess={handleSideDrawerSuccess}
        editingParcelIndex={editingParcelIndex}
        parcelToEdit={
          editingParcelIndex !== null
            ? (parcels[editingParcelIndex] as CargoOnboardParcelFormData)
            : undefined
        }
      />
    </div>
  );
};

export default Parcels;
